// src/components/UserLayout.js
import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useRegion } from '../context/RegionContext';
import { fetchRegions, API_BASE_URL } from '../services/api';
import './Layout.css'; // reuse existing styles
import ChatWidget from './ChatWidget';
import { auth } from '../firebaseConfig';

function UserLayout({ children }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableRegions, setAvailableRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const { selectedRegion, updateRegion, allowedRegions, canAccessRegion } = useRegion();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    loadRegions();
  }, []);
 
  // Wait for Firebase to restore the session before calling /connections —
  // otherwise auth.currentUser is null at mount and x-user-email goes out
  // empty, which the backend rejects with 400.
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) loadCompanyLogo(firebaseUser);
    });
    return () => unsub();
  }, [selectedRegion]);
 
  const loadCompanyLogo = async (firebaseUser) => {
    try {
      const u = firebaseUser || auth.currentUser;
      if (!u?.email) return;
      const token = await u.getIdToken();
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-email': u.email,
          'x-region': selectedRegion || 'vquest',   // ← the missing header
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      setCompanyLogo(data?.company?.LogoUrl || null);
      setCompanyName(data?.company?.Name || '');
    } catch (err) {
      console.error('Error loading company logo:', err);
    }
  };

  const loadRegions = async () => {
    try {
      const regions = await fetchRegions();
      setAvailableRegions(regions);
    } catch (error) {
      console.error('❌ Error loading regions:', error);
      setAvailableRegions([
        { id: 'vquest', name: 'New Zealand' },
        { id: 'vquest-au-1', name: 'Australia' },
        { id: 'vquest-irl-1', name: 'Ireland' },
        { id: 'vquest-us-1', name: 'USA/Canada' }
      ]);
    } finally {
      setRegionsLoading(false);
    }
  };

  const handleRegionChange = (e) => {
    const newRegionId = e.target.value;

    if (allowedRegions.length > 0 && !canAccessRegion(newRegionId)) {
      alert(`Access denied to region: ${newRegionId}. Contact your administrator for access.`);
      return;
    }

    updateRegion(newRegionId);
    setTimeout(() => window.location.reload(), 100);
  };

  const getUserAccessibleRegions = () => {
    if (allowedRegions.length === 0) return availableRegions;
    return availableRegions.filter((r) => allowedRegions.includes(r.id));
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') handleSearch();
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">
          <Link to="/user/dashboard">
            <img src="/icons/Nezo_Logo_White.png" alt="Logo" />
          </Link>
        </div>
        <hr />

        {/* Dashboard */}
        <NavLink
          to="/user/dashboard"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
          end
        >
          <img src="/icons/computer.png" alt="Dashboard Icon" />
          Dashboard
        </NavLink>

        <hr />
        {/* Material Library */}
        <NavLink
          to="/categories"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/library.png" alt="Material Library Icon" />
          Material Library
        </NavLink>

        {/* Build New Composite */}
        <NavLink
          to="/composite-requests"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/components.png" alt="Build New Composite Icon" />
          Build Material
        </NavLink>

        {/* My Composites */}
        <NavLink
          to="/user/my-composites"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/list.png" alt="My Composites Icon" />
          My Materials
        </NavLink>

        {/* Submit Material */}
        <NavLink
          to="/material-requests"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/submit.png" alt="Submit Material" />
          Submit Product 
        </NavLink>

        {/* EPD's */}
        <NavLink
          to="/user/epd"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/computer.png" alt="EPD Icon" />
          EPD's
        </NavLink>

        {/* Calculators */}
        <NavLink
          to="/user/calculator"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/calculator.png" alt="Calculator Icon" />
          Calculators
        </NavLink>

        {/* Reports */}
        <NavLink
          to="/user/reports"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/report.png" alt="Reports Icon" />
          Reports
        </NavLink>

        <hr />

        {/* Account (optional keep for user area) */}
        <NavLink
          to="/account"
          className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
        >
          <img src="/icons/person.png" alt="Account Icon" />
          Account
        </NavLink>

        <hr />

        {/* Nezo App */}
        <a
          href="https://app.nezo-app.com/"
          className="menu-item"
        >
          <img style={{ height: 24, width: 'auto' }} src="/icons/Nezo_Navy_Fabricon.png" alt="Nezo Icon" />
          Nezo App
        </a>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <div className="search-container">
            <input
              className="search-bar"
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button className="search-btn" onClick={handleSearch}>
              Search
            </button>
          </div>

          {/* Company logo (center) */}
          {companyLogo && (
            <div className="topbar-company-logo">
              <img src={companyLogo} alt={companyName ? `${companyName} logo` : 'Company logo'} />
            </div>
          )}

          <div className="top-buttons">
            {/* Region Dropdown */}
            <div className="region-dropdown-container">
              {regionsLoading ? (
                <div className="region-loading">🌏</div>
              ) : (
                <select
                  value={selectedRegion || ''}
                  onChange={handleRegionChange}
                  className="region-dropdown"
                  title="Switch Region"
                >
                  <option value="" disabled>
                    Select Region
                  </option>
                  {getUserAccessibleRegions().map((region) => (
                    <option key={region.id} value={region.id}>
                      🌏 {region.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button className="top-btn" title="Notifications">🔔</button>
            {/* ✅ FIX: Use Link directly instead of nesting Link inside button */}
            <Link to="/messages" className="top-btn" title="Messages">✉️</Link>
            <Link to="/account" className="top-btn" title="Account">👤</Link>
          </div>
        </div>

        {/* Page Content */}
        <div className="content-container">{children}</div>
      </div>
      <ChatWidget />
    </div>
  );
}

export default UserLayout;
